import { I18n } from "@assets/strings";
import React from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Carousel } from "@domain/carousel/carousel";
import { usePersonCarousels } from "@domain/carousel/hooks";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { useIsMobile } from "@utils/hooks";
import { Secondary } from "@ui/components/containers/textBlocks";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { stack } from "@ui/components/layout";
import { CarouselThumbnail } from "@ui/components/pdfCarousel/carouselThumbnail";
import { desktop, mobile } from "@ui/styles/utils";

interface ProfileCarouselsListProps extends React.HTMLAttributes<HTMLDivElement> {
	user: Person;
}

export const ProfileCarouselsList: React.FC<ProfileCarouselsListProps> = ({ user, ...htmlProps }) => {
	const isOrganization = user.type === "COMPANY" || user.type === "INSTITUTE";
	const {
		result: carousels,
		fetchMore,
		loading,
		totalElements,
		moreLoading,
	} = usePersonCarousels(user.id, isOrganization);

	const isMobile = useIsMobile();

	const isMedium = isMobile ? false : true;

	return totalElements === 0 && !loading ? (
		<Secondary>{I18n.t("profile.carousels.empty", { user: fullName(user) })}</Secondary>
	) : (
		<InfiniteScroll onEndReached={fetchMore} loading={loading || moreLoading}>
			<Carousels {...htmlProps}>
				{carousels.map((carousel: Carousel) =>
					carousel ? <CarouselThumbnail key={carousel.id} medium={isMedium} carousel={carousel} /> : undefined
				)}
				{loading || (moreLoading && <ClipLoader />)}
			</Carousels>
		</InfiniteScroll>
	);
};

const Carousels = styled.div`
	@media ${desktop} {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-gap: 20px;
	}
	@media ${mobile} {
		${stack(20, "center", "center")}
	}
`;
